import { isEmpty, typeOf } from '@ember/utils';
import { isEmptyOrEmptyObject } from './objects';

type QueryParam = [string, any];

// Query param creation. Needs to handle an object of:
// - strings  :: key=value
// - numbers  :: key=1
// - booleans :: key=true
// - arrays   :: key=one&key=two&key=three
// - objects  :: key[a]=one&key[b][c]=two&key[d]=three
function serializeParams(prefix: string, params: Record<string, any>): QueryParam[] {
  return Object.entries(params).reduce<QueryParam[]>((acc, [key, value]) => {
    let encodedKey = encodeURIComponent(key);
    let prefixedKey = encodedKey;

    // If this is being called recursively, then we're in a nested object and we
    // need to add brackets to the key
    if (prefix.length > 0) {
      prefixedKey = `${prefix}[${encodedKey}]`;
    }

    if (Array.isArray(value)) {
      // it doesn't make sense to support empty array values since our array
      // format is key=one&key=two&key=three
      let arrayParams = value.filter((v) => !isEmpty(v)).map<QueryParam>((v) => [prefixedKey, encodeURIComponent(v)]);

      return [...acc, ...arrayParams];
    }

    // @ts-ignore: object isn't a typed option
    if (typeOf(value) === 'object') {
      let nestedParams = serializeParams(prefixedKey, value);

      return [...acc, ...nestedParams];
    }

    return [...acc, [prefixedKey, value]];
  }, []);
}

export function buildQueryString(params: Record<string, any>, ignoreEmpty = false): string {
  // short circuit
  if (isEmptyOrEmptyObject(params)) {
    return '';
  }

  let paramPairs = serializeParams('', params);
  let filteredPairs = paramPairs.filter(([, value]) => {
    if (ignoreEmpty && isEmpty(value)) {
      return false;
    }

    return true;
  });
  let serialized = filteredPairs.map(([key, value]) => `${key}=${encodeURIComponent(value ?? '')}`);

  return serialized.join('&');
}

// set `ignoreEmptyParams` to true for backwards compatibility
export default function urlBuilder(base: string, params: Record<string, any>, ignoreEmptyParams = true): string {
  let paramString = buildQueryString(params, ignoreEmptyParams);

  if (paramString.length === 0) {
    return base;
  }

  return `${base}?${paramString}`;
}
